import React, { useEffect, useMemo, useState } from 'react';
import api from '../../system/client/src/services/api';

/* Shared hierarchical HS2/HS4 code picker.
 * Value is a flat array of code strings — level derived from length
 * (2 chars = HS2 chapter, 4 chars = HS4 heading).
 * Selecting an HS2 chapter covers all of its HS4 headings; clicking a
 * covered child swaps the chapter selection for "all siblings except
 * this one" so users can drill down naturally. */

let cachedTree = null;
let inflight = null;

export const loadHsTree = async () => {
    if (cachedTree) return cachedTree;
    if (inflight) return inflight;
    inflight = api
        .get('commodities/tree')
        .then((data) => {
            cachedTree = (data || []).filter(
                (c) => c.hs2_description && c.hs2_description.trim(),
            );
            return cachedTree;
        })
        .finally(() => {
            inflight = null;
        });
    return inflight;
};

export const useHsTree = () => {
    const [tree, setTree] = useState(cachedTree || []);
    useEffect(() => {
        if (cachedTree) return;
        let cancelled = false;
        loadHsTree().then((t) => {
            if (!cancelled) setTree(t);
        });
        return () => { cancelled = true; };
    }, []);
    return tree;
};

export const buildCodeLabelMap = (tree) => {
    const m = new Map();
    (tree || []).forEach((ch) => {
        m.set(ch.hs2_code, ch.hs2_description);
        (ch.children || []).forEach((c) => {
            m.set(c.hs4_code, c.hs4_description);
        });
    });
    return m;
};

const HsCodeTree = ({ value, onChange, disabledValues, query: externalQuery, maxHeight }) => {
    const tree = useHsTree();
    const [expanded, setExpanded] = useState(() => new Set());
    const [internalQuery, setInternalQuery] = useState('');
    const query = externalQuery !== undefined ? externalQuery : internalQuery;

    const selected = useMemo(() => new Set(value || []), [value]);
    const disabled = useMemo(() => new Set(disabledValues || []), [disabledValues]);

    const isChildDisabled = (chapter, code) =>
        disabled.has(code) || disabled.has(chapter.hs2_code);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) {
            return tree.map((ch) => ({ chapter: ch, children: ch.children, forceOpen: false }));
        }
        // numeric queries prefix-match codes ("84" → chapter 84 + 84xx, not 2841)
        const numeric = /^\d+$/.test(q);
        const codeHit = (code) => (numeric ? code.startsWith(q) : code.includes(q));
        const out = [];
        for (const ch of tree) {
            const chapterHit =
                codeHit(ch.hs2_code) ||
                (!numeric && (ch.hs2_description || '').toLowerCase().includes(q));
            const matchingChildren = (ch.children || []).filter(
                (c) =>
                    codeHit(c.hs4_code) ||
                    (!numeric && (c.hs4_description || '').toLowerCase().includes(q)),
            );
            if (chapterHit) {
                out.push({ chapter: ch, children: ch.children, forceOpen: false });
            } else if (matchingChildren.length) {
                out.push({ chapter: ch, children: matchingChildren, forceOpen: true });
            }
        }
        return out;
    }, [tree, query]);

    const toggleExpand = (hs2) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(hs2)) next.delete(hs2);
            else next.add(hs2);
            return next;
        });
    };

    const toggleChapter = (chapter) => {
        const hs2 = chapter.hs2_code;
        if (disabled.has(hs2)) return;
        const childCodes = (chapter.children || []).map((c) => c.hs4_code);
        if (selected.has(hs2)) {
            onChange((value || []).filter((c) => c !== hs2));
        } else {
            // selecting the chapter subsumes any individually-selected children
            const next = (value || []).filter((c) => !childCodes.includes(c));
            onChange([...next, hs2]);
        }
    };

    const toggleChild = (chapter, hs4) => {
        if (isChildDisabled(chapter, hs4)) return;
        const hs2 = chapter.hs2_code;
        if (selected.has(hs2)) {
            // chapter covers this child — swap to all siblings except this one
            const siblings = (chapter.children || [])
                .map((c) => c.hs4_code)
                .filter((c) => c !== hs4 && !isChildDisabled(chapter, c));
            const next = (value || []).filter((c) => c !== hs2);
            onChange([...next, ...siblings]);
        } else if (selected.has(hs4)) {
            onChange((value || []).filter((c) => c !== hs4));
        } else {
            onChange([...(value || []), hs4]);
        }
    };

    return (
        <div className="hs-tree">
            {externalQuery === undefined && (
                <input
                    type="text"
                    className="commodity-picker-search"
                    placeholder="Search codes or descriptions…"
                    value={internalQuery}
                    onChange={(e) => setInternalQuery(e.target.value)}
                />
            )}
            <div className="commodity-picker-list" style={maxHeight ? { maxHeight } : undefined}>
                {tree.length === 0 ? (
                    <div className="commodity-picker-empty">Loading commodity codes…</div>
                ) : visible.length === 0 ? (
                    <div className="commodity-picker-empty">No matches</div>
                ) : (
                    visible.map(({ chapter, children, forceOpen }) => {
                        const hs2 = chapter.hs2_code;
                        const open = forceOpen || expanded.has(hs2);
                        const chapterChecked = selected.has(hs2);
                        const selectedChildCount = (chapter.children || []).filter((c) =>
                            selected.has(c.hs4_code),
                        ).length;
                        const indeterminate = !chapterChecked && selectedChildCount > 0;
                        const chapterDisabled = disabled.has(hs2);

                        return (
                            <div key={hs2} className="hs-tree-chapter">
                                <div
                                    className={`commodity-picker-row hs-tree-row${chapterChecked ? ' checked' : ''}${chapterDisabled ? ' disabled' : ''}`}
                                >
                                    <button
                                        type="button"
                                        className={`hs-tree-chevron${open ? ' open' : ''}`}
                                        aria-label={open ? 'Collapse' : 'Expand'}
                                        onClick={() => toggleExpand(hs2)}
                                        disabled={!(chapter.children || []).length}
                                    >
                                        ▸
                                    </button>
                                    <button
                                        type="button"
                                        className="hs-tree-toggle"
                                        onClick={() => toggleChapter(chapter)}
                                        disabled={chapterDisabled}
                                    >
                                        <span
                                            className={`commodity-picker-check${chapterChecked ? ' on' : ''}${indeterminate ? ' partial' : ''}`}
                                        >
                                            {chapterChecked ? '✓' : indeterminate ? '–' : ''}
                                        </span>
                                        <span className="commodity-picker-code">{hs2}</span>
                                        <span className="commodity-picker-desc">
                                            {chapter.hs2_description}
                                        </span>
                                        {indeterminate && (
                                            <span className="hs-tree-count">
                                                {selectedChildCount} selected
                                            </span>
                                        )}
                                    </button>
                                </div>
                                {open &&
                                    (children || []).map((child) => {
                                        const hs4 = child.hs4_code;
                                        const covered = chapterChecked;
                                        const childChecked = covered || selected.has(hs4);
                                        const childDisabled = isChildDisabled(chapter, hs4);
                                        return (
                                            <div
                                                key={hs4}
                                                className={`commodity-picker-row hs-tree-row hs-tree-child${childChecked ? ' checked' : ''}${childDisabled ? ' disabled' : ''}`}
                                            >
                                                <button
                                                    type="button"
                                                    className="hs-tree-toggle"
                                                    onClick={() => toggleChild(chapter, hs4)}
                                                    disabled={childDisabled}
                                                >
                                                    <span
                                                        className={`commodity-picker-check${childChecked ? ' on' : ''}${covered ? ' covered' : ''}`}
                                                    >
                                                        {childChecked ? '✓' : ''}
                                                    </span>
                                                    <span className="commodity-picker-code hs4">
                                                        {hs4}
                                                    </span>
                                                    <span className="commodity-picker-desc">
                                                        {child.hs4_description || '—'}
                                                    </span>
                                                </button>
                                            </div>
                                        );
                                    })}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default HsCodeTree;
